<script setup lang="ts">
import { AfCardTooltip, AfSelect, AfTextfield } from '@af-lib/ui-lib';
import { ALLink, ALButton } from '@af-lib/vue-ui';
import { computed, inject, ref } from 'vue';

import { useHubspotTicket } from '@/api/vue-query/hubspot/hubspot.mutations';
import { MAX_CHARS } from '@/enums/constants';
import { useAuthStore } from '@/stores/auth';
import { useLocaleStore } from '@/stores/locales';
import type { Translate } from '@/types/i18n/i18n.types';

import AfUploadFiles from './AfUploadFiles.vue';
import { testMailValidity } from '@/helpers/generic.ts';

const t = inject('t') as Translate;
const emit = defineEmits<{
  close;
}>();

const { mutate, isPending } = useHubspotTicket();

const authStore = useAuthStore();
const files = ref<Blob[]>([]);
const MAX_FILES_UPLOAD = 1;
const MAX_FILE_SIZE = 5 * 1048576; // 5 MB
const MAX_FILE_SIZE_MB = 5;
const textfieldElement = ref();
const email = ref(authStore?.user?.email ?? '');
const request = ref('');
const message = ref('');
const messageExceeded = ref(false);
const optionsList = ref([
  {
    title: t('general.support.widget.request.option_1'),
    value: 'Request'
  },
  {
    title: t('general.support.widget.request.option_2'),
    value: 'Bug'
  },
  {
    title: t('general.support.widget.request.option_3'),
    value: 'Improvement'
  }
]);

const canSubmit = computed(() => {
  return (
    email.value?.trim() !== '' && request.value?.trim() !== '' && message.value?.trim() !== '' && !messageExceeded.value
  );
});
const locale = computed(() => useLocaleStore().getCurrentISO ?? 'en-US');
const userUuid = computed(() => authStore?.user?.uuid ?? '');
const companyUuid = computed(() => authStore.company_uuid ?? '');

function onChangeInput(value) {
  if (testMailValidity(value)) {
    email.value = value;
    textfieldElement.value.setError({ isError: false });
  } else {
    email.value = '';
    textfieldElement.value.setError({ isError: true, errorMessage: t('general.error.email.format') });
  }
}
function onChangeMessage(value: string) {
  message.value = value.length ? value : '';
}
function updateSelect(item) {
  request.value = item.value;
}
function onMaxCharExceeded(isExceeded: boolean) {
  messageExceeded.value = !isExceeded;
}

function submit() {
  if (!canSubmit.value) return;
  const formData = new FormData();

  formData.append('subject', `Phoenix - ${email.value}`);
  formData.append('content', message.value);
  formData.append('category', 'GENERAL_INQUIRY');
  formData.append('question_type', request.value);
  formData.append('email', email.value);
  formData.append('locale', locale.value);
  formData.append('url', window.location.href);
  formData.append('user_uuid', userUuid.value);
  formData.append('company_uuid', companyUuid.value);
  formData.append('technical_informations', navigator.userAgent);
  formData.append('user_permission', '-');
  formData.append('pipeline_stage', '1');
  formData.append('ticket_priority', 'MEDIUM');
  formData.append('device', `${window.innerWidth} x ${window.innerHeight}`);

  files.value.forEach(file => {
    formData.append('files_upload[]', file);
  });

  mutate(formData, {
    onSettled: async () => {
      emit('close');
    }
  });
}
</script>

<template>
  <div class="form-support rounded border bg-surface">
    <div class="header py-2 px-5 d-flex align-center">
      <h2 class="text-h4 text-quaternary d-flex align-center gap-8" data-cy="support-form-title">
        <v-icon icon="fas fa-headset" size="xs" />
        {{ t('general.support.widget.title') }}
      </h2>
      <button
        class="d-flex ms-auto"
        :aria-label="t('general.close')"
        data-cy="support-form-close"
        @click="emit('close')"
      >
        <v-icon size="21" icon="fat fa-xmark" color="primaryIcon" />
      </button>
    </div>
    <v-divider />
    <div class="bg-sectionSecondaryBg py-3 px-5 d-flex align-center gap-8">
      <h2 class="text-body-3 font-weight-bold" data-cy="support-form-subtitle">
        {{ t('general.support.title') }}
      </h2>
      <ALLink :label="t('general.support.faq_plain')" :to="t('general.support.url')" size="m" />
    </div>
    <v-divider />
    <div class="form-content pt-4 px-5 d-grid gap-12">
      <AfTextfield
        id="support-email"
        ref="textfieldElement"
        type="input"
        fieldType="open"
        :placeholder="t('general.placeholder.email_one')"
        :label="t('general.email_required', { email: 'required' })"
        :clearable="true"
        :defaultInput="email"
        data-cy="support-email-input"
        @updateInput="onChangeInput"
      />
      <AfSelect
        class="select"
        :label="t('general.support.widget.request.title')"
        :placeholder="t('general.support.widget.request.placeholder')"
        :itemList="optionsList"
        :required="true"
        @update:selected="updateSelect"
      ></AfSelect>
      <AfTextfield
        id="support-email"
        type="textarea"
        fieldType="open"
        :placeholder="t('general.support.widget.textbox.placeholder')"
        :label="t('general.support.widget.textbox.title')"
        :clearable="true"
        :maxChar="MAX_CHARS.hubspot_message"
        :defaultInput="message"
        data-cy="support-email-input"
        @updateInput="onChangeMessage"
        @maxCharExceeded="onMaxCharExceeded"
      />
      <div>
        <div class="d-grid align-center mb-2 text-body-3 text-fontSecondary gap-8">
          <p class="d-flex align-center gap-8">
            {{ t('general.media.upload.title') }}
            <AfCardTooltip
              :title="t('general.image.constraint')"
              titleIcon="fat fa-info-circle"
              icon="fa-info-circle"
              iconSize="text-icon-4"
              location="bottom"
              tooltipSize="large"
            >
              <template #text>
                <p class="text-fontSecondary">{{ t('general.image.title') }}</p>
                <ul class="ps-3">
                  <li>{{ t('general.image.constraint_1') }}</li>
                  <li>
                    {{ t('general.image.constraint_3', { max_size: MAX_FILE_SIZE_MB }) }}
                  </li>
                </ul>
              </template>
            </AfCardTooltip>
          </p>
        </div>
        <AfUploadFiles
          id="screen"
          :size="MAX_FILE_SIZE"
          :maxFilesUpload="MAX_FILES_UPLOAD"
          datacyPrefix="support-screen"
          @imageUpload="files = $event"
        />
      </div>
    </div>
    <div class="d-flex align-center gap-20 justify-center py-4 px-5">
      <ALButton type="tertiary" data-cy="support-form-cancel" :label="t('general.cancel')" @click="emit('close')" />
      <ALButton :isDisabled="!canSubmit" :isLoading="isPending" :label="t('general.send')" @click="submit" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.form-support {
  width: 24rem;
  max-height: calc(100vh - 95px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.form-content {
  flex: 1;
  overflow-y: scroll;
}
</style>
